import { useState } from "react";
import { Avatar, AvatarImage, AvatarFallback, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, Menu, LogOut, ChevronDown, UserRound, Sparkles } from "bizpandit-ui";
import { useQuery } from "@tanstack/react-query";
import { useAccess } from "@/features/access/AccessProvider";
import AssistantPanel from "@/features/assistant/AssistantPanel";
import { assistantService } from "@/features/assistant/assistant.service";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/app/constants";
import NotificationBell from "@/features/notifications/NotificationBell";
import { useLogout } from "@/features/auth/hooks/useLogout";
import type { AuthUser } from "@/features/auth/types";

interface NavbarProps {
  user: AuthUser | null;
  onToggleSidebar: () => void;
}

function getInitials(name?: string | null): string {
  if (!name) return "?";
  
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

export default function Navbar({ user, onToggleSidebar }: NavbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const logout = useLogout();
  const navigate = useNavigate();
  const { hasPermission } = useAccess();
  const [assistantOpen, setAssistantOpen] = useState(false);
  // Decision #21: offered to holders of assistant:use once the workspace has
  // switched it on; an admin sees it either way, to find the switch.
  const mayUse = hasPermission("assistant:use");
  const assistant = useQuery({ queryKey: ["assistant-status"], queryFn: assistantService.status, enabled: mayUse, staleTime: 60_000, retry: false });
  const showAssistant = mayUse && (assistant.data?.workspaceEnabled || hasPermission("assistant:admin"));

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-linegray bg-white px-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onToggleSidebar}
          className="rounded-lg p-2 text-navy2/70 transition-colors hover:bg-surface hover:text-navy2"
          aria-label="Toggle sidebar"
        >
          <Menu className="h-5 w-5" />
          
        </button>
      </div>

      <div className="flex items-center gap-2">
        {/* Was an icon with a permanent red dot that meant nothing. It reads
            the real inbox now — Dashboard §6.1. */}
        {showAssistant && (
          <button
            type="button"
            onClick={() => setAssistantOpen(true)}
            className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-navy2/80 transition-colors hover:bg-surface hover:text-navy2"
            aria-label="Open the assistant"
          >
            <Sparkles className="h-4 w-4 text-rose" />
            <span className="hidden md:inline">Assistant</span>
          </button>
        )}
        {showAssistant && <AssistantPanel open={assistantOpen} onOpenChange={setAssistantOpen} />}
        <NotificationBell />

         <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-surface"
            >
              <Avatar size="sm">
                <AvatarImage src={user?.avatarUrl ?? undefined} alt={user?.name ?? "Account"} />
                <AvatarFallback>{getInitials(user?.name)}</AvatarFallback>
              </Avatar>
              <span className="hidden text-sm font-medium text-navy2 sm:block">
                {user?.name ?? "Account"}
              </span>
              <ChevronDown className="h-4 w-4 text-navy2/50" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled className="text-xs text-navy2/50">
              {user?.email}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {/* The second hat (A9): a member of staff who is also a client
                opens their own portal on the same login. */}
            {user?.contactId && (
              <>
                <DropdownMenuItem onSelect={() => navigate(ROUTES.portal)} className="gap-2">
                  <UserRound className="h-4 w-4" /> My client portal
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onSelect={() => logout.mutate()} className="gap-2 text-caution">
              <LogOut className="h-4 w-4" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
